import {ActionProvider} from '@github-ui/code-view-shared/contexts/ActionContext'
import {AllShortcutsEnabledProvider} from '@github-ui/code-view-shared/contexts/AllShortcutsEnabledContext'
import {CodeViewBannersProvider} from '@github-ui/code-view-shared/contexts/CodeViewBannersContext'
import {EditPermissionsProvider} from '@github-ui/code-view-shared/contexts/EditPermissionsContext'
import {FileQueryProvider} from '@github-ui/code-view-shared/contexts/FileQueryContext'
import {OpenPanelProvider} from '@github-ui/code-view-shared/contexts/OpenPanelContext'
import {PathProvider} from '@github-ui/code-view-shared/contexts/PathContext'
import {RefInfoProvider} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import type {LayoutPayload, ReposAppPayload} from '@github-ui/code-view-types'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {AppPayloadContext} from '@github-ui/react-core/use-app-payload'
import type {UseSuspenseQueryResult} from '@github-ui/react-query'
import {Outlet, SoftNavOnlyLink as Link, useMatches} from '@github-ui/react-router'
import {
  AbuseReportBanner,
  ContentWarningBanner,
  IdentityManagementBanner,
  VerifiableDomainsBanner,
} from '@github-ui/repo-layout'
import {FileTreeControlProvider, useTreePane} from '@github-ui/repos-file-tree-view'
import {isUIService} from '@github-ui/runtime-environment'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize} from '@github-ui/screen-size'
import {GlobalCommands} from '@github-ui/ui-commands'
import {useCallback, useRef} from 'react'

import {AppWrapper} from '../App'
import {OrgSuccessorBanner} from '../components/banners/org-successor-banner/OrgSuccessorBanner'
import {PreReceiveHookFailureBanner} from '../components/banners/pre-receive-hook-failure-banner/PreReceiveHookFailureBanner'
import {RepositoryMigratingNotice} from '../components/banners/repository-migrating-notice/RepositoryMigratingNotice'
import {TradeControlsBanner} from '../components/banners/trade-controls-banner/TradeControlsBanner'
import {symbolsHeaderId} from '../components/blob/BlobContent/CodeNav/CodeNavSymbolNavigation'
import {ScrollMarksContainer} from '../components/blob/BlobContent/CodeNav/ScrollMarksContainer'
import {BudgetThresholdBannerSlot} from '../components/BudgetThresholdBannerSlot'
import {OpenExternalEditorCommands} from '../components/OpenExternalEditorCommands'
import {PermalinkShortcut} from '../components/PermalinkShortcut'
import {RevertErrorBanner} from '../components/RevertErrorBanner/RevertErrorBanner'
import {REPOS_FILE_TREE_SIDEBAR_ID, REPOS_SPLIT_PANE_CONTENT_ID} from '../components/SharedPageLayout'
import {CodeNavProvider} from '../contexts/CodeNavContext'
import {TreePaneProvider} from '../contexts/TreePaneContext'
import {useRouteAction} from '../hooks/use-route-action'
import {updateExpandPreferences} from '../hooks/use-update-panel-expand-preferences'
import {codeViewLayoutRoute} from '../routes/code-view-layout-route'
import {codeViewRepoRoute} from '../routes/code-view-repo-route'
import {textAreaId} from '../utilities/lines'
import {CodeViewEmptyLayout} from './CodeViewEmptyLayout'

const REPOS_FILE_TREE_ID = 'repos-file-tree'

function updateTreeExpandedPreference(isTreeExpanded: boolean) {
  updateExpandPreferences(isTreeExpanded, null, null)
}

export function CodeViewLayout() {
  const {data} = useRouteQuery(codeViewLayoutRoute, 'mainQuery')
  const {data: appPayload} = useLayoutAppPayload()
  const matches = useMatches()
  const isRepoIndexRoute = matches.some(match => match.id === codeViewRepoRoute.id)

  return (
    <AppPayloadContext value={appPayload}>
      <AppWrapper payload={data} repo={data.repo}>
        {/*
         This hidden link exposes the repository route as a stable DOM hook.
         It is used for programmatic navigation when clicking on the Code tab.
       */}
        <Link
          to={codeViewRepoRoute.generatePath({owner: data.repo.ownerLogin, repo: data.repo.name})}
          hidden
          id="code-view-repo-link"
        />

        {isUIService && data.identityManagementBanner && (
          <IdentityManagementBanner
            key={data.identityManagementBanner.organizationId}
            {...data.identityManagementBanner}
            returnFocusIds={[REPOS_SPLIT_PANE_CONTENT_ID, REPOS_FILE_TREE_SIDEBAR_ID]}
          />
        )}
        {isUIService && <BudgetThresholdBannerSlot />}
        {isUIService && <PreReceiveHookFailureBanner hookFailure={data.hookFailure} />}
        {isUIService && data.verifiableDomainsNotification && (
          <VerifiableDomainsBanner {...data.verifiableDomainsNotification} />
        )}
        {isUIService && data.contentWarning && (
          <ContentWarningBanner
            contentWarning={data.contentWarning}
            owner={data.repo.ownerLogin}
            repo={data.repo.name}
            returnFocusIds={[REPOS_SPLIT_PANE_CONTENT_ID, REPOS_FILE_TREE_SIDEBAR_ID]}
          />
        )}
        {isUIService && data.archivedTradeControlsNotice && (
          <TradeControlsBanner
            key={`${data.repo.ownerLogin}/${data.repo.name}`}
            notice={data.archivedTradeControlsNotice}
            returnFocusIds={[REPOS_SPLIT_PANE_CONTENT_ID, REPOS_FILE_TREE_SIDEBAR_ID]}
          />
        )}
        {isUIService && (
          <RepositoryMigratingNotice
            migratingNotice={data.migratingNotice}
            ownerLogin={data.repo.ownerLogin}
            returnFocusIds={[REPOS_SPLIT_PANE_CONTENT_ID, REPOS_FILE_TREE_SIDEBAR_ID]}
          />
        )}
        {isUIService && data.abuseReportBanner && <AbuseReportBanner {...data.abuseReportBanner} />}
        {isUIService && <RevertErrorBanner revertError={data.revertError} />}
        {isUIService && data.orgSuccessorBanner && (
          <OrgSuccessorBanner
            key={data.orgSuccessorBanner.organizationId}
            {...data.orgSuccessorBanner}
            returnFocusIds={[REPOS_SPLIT_PANE_CONTENT_ID, REPOS_FILE_TREE_SIDEBAR_ID]}
          />
        )}
        {data.repo.isEmpty && isRepoIndexRoute ? (
          <CodeViewEmptyLayout data={data} />
        ) : (
          <CodeViewLayoutInternal data={data} />
        )}
      </AppWrapper>
    </AppPayloadContext>
  )
}

function useLayoutAppPayload(): UseSuspenseQueryResult<ReposAppPayload> {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- assertion required for type narrowing; removing it breaks tsc
  return useRouteQuery(codeViewLayoutRoute, 'mainQuery', {
    select: ({payload: {helpUrl, githubDevUrl}}) => ({
      helpUrl,
      githubDevUrl,
    }),
  } as Parameters<typeof useRouteQuery>[2]) as UseSuspenseQueryResult<ReposAppPayload>
}

function CodeViewLayoutInternal({data}: {data: LayoutPayload}) {
  const openPanelRef = useRef<string | undefined>(undefined)
  // Tracks whether the Copilot side panel is open. The panel lives below this
  // component, so it writes into this ref via TreePaneContext. useTreePane reads
  // it to treat the Copilot panel like the symbol viewer when collapsing the
  // file nav on narrow screens.
  const copilotPanelOpenRef = useRef(false)
  const treeRef = useRef<HTMLDivElement>(null)
  // when user presses cmd+f6 this controls whether we focus the tree or the content
  const contentFocusedRef = useRef(false)
  const textAreaFocusedRef = useRef(false)
  // when we focus the content we can try to return focus to the element which previously had it
  const contentFocusTargetRef = useRef<HTMLElement | null>(null)
  // when we focus the tree we can try to return focus to the element which previously had it
  const treeFocusTargetRef = useRef<HTMLElement | null>(null)

  const treePaneState = useTreePane(
    REPOS_FILE_TREE_ID,
    openPanelRef,
    data.treeExpanded,
    updateTreeExpandedPreference,
    undefined,
    undefined,
    copilotPanelOpenRef,
  )
  const {expandTree} = treePaneState

  const onFindFilesShortcut = useCallback(() => {
    if (window.matchMedia(`(max-width: ${ScreenSize.large - 1}px)`).matches) {
      expandTree({focus: 'search'})
    }
  }, [expandTree])

  function toggleFocus() {
    const contentElement = document.querySelector<HTMLDivElement>('[data-selector="repos-split-pane-content"]')
    const symbolsPaneElement = document.getElementById(symbolsHeaderId)
    const textAreaElement = document.getElementById(textAreaId)
    if (document.activeElement?.id === textAreaId) {
      textAreaFocusedRef.current = true
    }
    // the user may have moved focus from where we last put it
    if (contentElement?.contains(document.activeElement) && !textAreaFocusedRef.current) {
      //the content (but not the text area) is focused
      contentFocusedRef.current = true
    } else if (treeRef.current?.contains(document.activeElement)) {
      contentFocusedRef.current = false
    }
    if (!contentFocusedRef.current && !textAreaFocusedRef.current) {
      // focus the text area
      const focusTarget = textAreaElement || contentElement
      treeFocusTargetRef.current = treeRef.current?.contains(document.activeElement)
        ? (document.activeElement as HTMLElement)
        : null
      contentFocusedRef.current = false
      focusTarget?.focus()
    } else if (textAreaFocusedRef.current) {
      //focus the content
      const focusTarget = contentFocusTargetRef.current || symbolsPaneElement || contentElement
      contentFocusedRef.current = true
      textAreaFocusedRef.current = false
      focusTarget?.focus()
    } else {
      // focus the tree
      const focusTarget = treeFocusTargetRef.current || treeRef.current
      contentFocusTargetRef.current = contentElement?.contains(document.activeElement)
        ? (document.activeElement as HTMLElement)
        : null
      contentFocusedRef.current = false
      textAreaFocusedRef.current = false
      focusTarget?.focus()
    }
  }

  const action = useRouteAction()

  return (
    <AllShortcutsEnabledProvider allShortcutsEnabled={data.allShortcutsEnabled}>
      <FileTreeControlProvider>
        <FileQueryProvider>
          <CodeNavProvider>
            <TreePaneProvider
              treePaneState={treePaneState}
              onFindFilesShortcut={onFindFilesShortcut}
              copilotPanelOpenRef={copilotPanelOpenRef}
            >
              <PathProvider path={data.path}>
                <ActionProvider action={action}>
                  <RefInfoProvider refInfo={data.refInfo}>
                    <EditPermissionsProvider
                      canEdit={data.refInfo.canEdit}
                      canEditOnDefaultBranch={data.refInfo.canEditOnDefaultBranch}
                      fileExistsOnDefault={data.refInfo.fileExistsOnDefault}
                    >
                      <PermalinkShortcut />
                      <div>
                        <OpenPanelProvider payload={data} openPanelRef={openPanelRef} action={action}>
                          <CodeViewBannersProvider>
                            <Outlet />
                          </CodeViewBannersProvider>
                        </OpenPanelProvider>
                        <ScrollMarksContainer />
                        {/* TODO: make this focus on the cursor instead of just the blob as a whole */}
                        <GlobalCommands commands={{'code-view:toggle-focused-pane': toggleFocus}} />
                        <OpenExternalEditorCommands />
                      </div>
                    </EditPermissionsProvider>
                  </RefInfoProvider>
                </ActionProvider>
              </PathProvider>
            </TreePaneProvider>
          </CodeNavProvider>
        </FileQueryProvider>
      </FileTreeControlProvider>
    </AllShortcutsEnabledProvider>
  )
}
